import React, { useState } from "react";
import { useGetOrderbookQuery } from "../../redux/slices/orderbook/orderbookApi";
import { Header, ExportCSVButton } from "../../components";
import SearchBox from "../../components/SearchBox";
import { useStateContext } from "../../contexts/ContextProvider";
import Pagination from "../../components/Pagination";

const OrderbookPage = () => {
  const { data, isLoading, error } = useGetOrderbookQuery();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");
  const { currentMode } = useStateContext();

  if (isLoading) {
    return (
      <div
        className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${
          currentMode === "Dark" ? "bg-secondary-dark-bg" : "bg-white"
        }`}
      >
        <Header category="Page" title="Orderbook" />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${
          currentMode === "Dark" ? "bg-secondary-dark-bg" : "bg-white"
        }`}
      >
        <Header category="Page" title="Orderbook" />
        <div className="flex items-center justify-center py-16">
          <div
            className={`text-center rounded-lg p-8 max-w-md mx-auto border ${
              currentMode === "Dark"
                ? "bg-gray-800 border-gray-600"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="mb-4">
              <svg
                className="w-16 h-16 mx-auto text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3
              className={`text-lg font-semibold mb-2 ${
                currentMode === "Dark" ? "text-white" : "text-gray-700"
              }`}
            >
              Unable to Load Orderbook
            </h3>
            <p
              className={`text-sm mb-4 ${
                currentMode === "Dark" ? "text-gray-300" : "text-gray-500"
              }`}
            >
              We encountered an issue while loading the orderbook data. Please
              try refreshing the page or contact support if the problem
              persists.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  const orderbook = data?.transactions || [];

  // Filter by scripcode or tag
  const filtered = orderbook.filter(
    (item) =>
      item.scripcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tag?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort function
  const sortRecords = (dataRecords, field, direction) =>
    [...dataRecords].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      // Handle string fields
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";

      if (direction === "asc") {
        if (aValue > bValue) return 1;
        if (aValue < bValue) return -1;
        return 0;
      }
      if (aValue < bValue) return 1;
      if (aValue > bValue) return -1;
      return 0;
    });

  // Apply sorting to filtered records
  const sortedRecords = sortRecords(filtered, sortField, sortDirection);

  // Handle sort column click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Get sort indicator
  const getSortIndicator = (field) => {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 ml-1 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }
    return sortDirection === "asc" ? (
      <svg
        className="w-4 h-4 ml-1 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        className="w-4 h-4 ml-1 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  // Pagination
  const indexOfLast = currentPage * recordsPerPage;
  const indexOfFirst = indexOfLast - recordsPerPage;
  const currentRecords = sortedRecords.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedRecords.length / recordsPerPage);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getRowBackgroundClass = (index) => {
    if (currentMode === "Dark") {
      return index % 2 === 0
        ? "bg-secondary-dark-bg"
        : "bg-secondary-dark-bg-light";
    }
    return index % 2 === 0 ? "bg-white" : "bg-gray-50";
  };

  const getStatusBadgeClass = (status) => {
    if (status === "PENDING") {
      return "bg-yellow-100 text-yellow-800";
    }
    if (status === "COMPLETED") {
      return "bg-green-100 text-green-800";
    }
    if (status === "CANCELLED") {
      return "bg-red-100 text-red-800";
    }
    return "bg-gray-100 text-gray-800";
  };

  const getTransactionTypeBadgeClass = (transactionType) => {
    if (transactionType === "BUY") {
      return "bg-green-100 text-green-800";
    }
    if (transactionType === "SELL") {
      return "bg-red-100 text-red-800";
    }
    return "bg-gray-100 text-gray-800";
  };

  return (
    <div
      className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${
        currentMode === "Dark" ? "bg-secondary-dark-bg" : "bg-white shadow-lg"
      }`}
    >
      <Header category="Page" title="Orderbook" />
      {/* Search Box and Export Button */}
      <div className="mb-4 flex items-center justify-between">
        <SearchBox
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by scripcode or tag..."
        />
        <ExportCSVButton
          data={sortedRecords}
          filename="orderbook.xlsx"
          buttonText="Export to Excel"
        />
      </div>
      {/* Records Count */}
      <div className="mb-4">
        <p className={currentMode === "Dark" ? "text-white" : "text-gray-600"}>
          Showing {sortedRecords.length === 0 ? 0 : indexOfFirst + 1} to{" "}
          {Math.min(indexOfLast, sortedRecords.length)} of{" "}
          {sortedRecords.length} records
        </p>
      </div>
      <div className="overflow-x-auto">
        <table
          className={`min-w-max border ${
            currentMode === "Dark"
              ? "bg-secondary-dark-bg border-gray-600"
              : "bg-white border-gray-300"
          }`}
        >
          <thead
            className={
              currentMode === "Dark"
                ? "bg-secondary-dark-bg-light"
                : "bg-gray-50"
            }
          >
            <tr>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("id")}
              >
                <div className="flex items-center">
                  Order ID
                  {getSortIndicator("id")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("user_ref_id")}
              >
                <div className="flex items-center">
                  User Nuqi ID
                  {getSortIndicator("user_ref_id")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("status")}
              >
                <div className="flex items-center">
                  Status
                  {getSortIndicator("status")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("scripcode")}
              >
                <div className="flex items-center">
                  Scrip Code
                  {getSortIndicator("scripcode")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("tag")}
              >
                <div className="flex items-center">
                  Tag
                  {getSortIndicator("tag")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("exchange_order_id")}
              >
                <div className="flex items-center">
                  Exchange Order ID
                  {getSortIndicator("exchange_order_id")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("platform")}
              >
                <div className="flex items-center">
                  Platform
                  {getSortIndicator("platform")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("quantity")}
              >
                <div className="flex items-center">
                  Quantity
                  {getSortIndicator("quantity")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("provider")}
              >
                <div className="flex items-center">
                  Provider
                  {getSortIndicator("provider")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("trade_platform")}
              >
                <div className="flex items-center">
                  Trading Platform
                  {getSortIndicator("trade_platform")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("trading_symbol")}
              >
                <div className="flex items-center">
                  Trading Symbol
                  {getSortIndicator("trading_symbol")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("transaction_type")}
              >
                <div className="flex items-center">
                  Transaction Type
                  {getSortIndicator("transaction_type")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("close")}
              >
                <div className="flex items-center">
                  Price
                  {getSortIndicator("close")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("compname")}
              >
                <div className="flex items-center">
                  Company Name
                  {getSortIndicator("compname")}
                </div>
              </th>
            </tr>
          </thead>
          <tbody
            className={`divide-y ${
              currentMode === "Dark"
                ? "bg-secondary-dark-bg divide-gray-600"
                : "bg-white divide-gray-200"
            }`}
          >
            {currentRecords.map((order, index) => (
              <tr key={order.id} className={getRowBackgroundClass(index)}>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                    currentMode === "Dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  {order?.id}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {order?.user_ref_id}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(
                      order?.status
                    )}`}
                  >
                    {order?.status}
                  </span>
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {order?.scripcode}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {order?.tag}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {order?.exchange_order_id || "-"}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {order?.platform || "-"}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {order?.quantity || "-"}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {order?.provider || "-"}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {order?.trade_platform || "-"}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {order?.trading_symbol || "-"}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${getTransactionTypeBadgeClass(
                      order?.transaction_type
                    )}`}
                  >
                    {order?.transaction_type || "-"}
                  </span>
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {order?.latestPrice.close || "-"}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {order?.stock?.compname || "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalRecords={sortedRecords.length}
        backendLimit={recordsPerPage}
        recordsCount={sortedRecords.length}
      />

      {/* No Records Message */}
      {sortedRecords.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <div
            className={`text-center rounded-lg p-8 max-w-md mx-auto border ${
              currentMode === "Dark"
                ? "bg-gray-800 border-gray-600"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="mb-4">
              <svg
                className={`w-16 h-16 mx-auto ${
                  currentMode === "Dark" ? "text-gray-500" : "text-gray-400"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            </div>
            <h3
              className={`text-lg font-semibold mb-2 ${
                currentMode === "Dark" ? "text-white" : "text-gray-700"
              }`}
            >
              No Orders Found
            </h3>
            <p
              className={`text-sm ${
                currentMode === "Dark" ? "text-gray-300" : "text-gray-500"
              }`}
            >
              {searchTerm
                ? "No orders match your search criteria. Try adjusting your search terms."
                : "There are currently no orders available."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderbookPage;
